/**
 * Every user-facing string in the Help Centre, in one place.
 *
 * House rules: active voice, sentence case, no filler. An empty screen invites the next action and
 * never stops at "nothing found"; an error says what happened and what to do next. The manual
 * describes the product, it never describes the sidebar as access control (the route guard is still
 * in observe mode).
 */

export const HC_COPY = {
	pageTitle: 'Help Centre',
	homeHeading: 'What do you want to do?',
	homeLede: 'Step-by-step guides for the parts of iElect you can use, written in the words the screens use.',
	searchPlaceholder: 'Search guides — e.g. “approve agents in bulk”',
	searchLabel: 'Search guides',
	searchSubmit: 'Search',
	roleScope: (role: string) => `Showing guides for ${role}`,
	roleScopeHint: 'Guides for modules you cannot open are not listed.',
	recentlyUpdated: 'Recently updated',
	guideCount: (n: number) => (n === 1 ? '1 guide' : `${n} guides`),

	unavailableTitle: 'The Help Centre isn’t available yet',
	unavailableBody: 'This server does not have the Help Centre installed. Everything else in iElect works as normal.',
	treeErrorTitle: 'Couldn’t load the guides',
	treeErrorBody: 'The Help Centre did not answer. Check your connection and try again.',
	retry: 'Try again',
	emptyTitle: 'No guides published for your role yet',
	emptyBody: 'Try a search — it covers every guide you can open, including ones still being organised.',

	article: {
		backToHelp: 'All guides',
		inThisGuide: 'In this guide',
		moreIn: (module: string) => `More in ${module}`,
		steps: (n: number) => (n === 1 ? '1 step' : `${n} steps`),
		minutes: (n: number) => `~${n} min`,
		updated: (date: string) => `Updated ${date}`,
		beforeYouStart: 'Before you start',
		youllSee: 'You’ll see',
		stepLabel: (n: number, total: number) => `Step ${n} of ${total}`,
		noScreenshot: 'No screenshot for this step',
		enlarge: (n: number) => `Enlarge screenshot for step ${n}`,
		notFoundTitle: 'This guide isn’t available',
		notFoundBody: 'It may have been moved or unpublished, or it covers a module your role cannot open.',
		errorTitle: 'Couldn’t load this guide',
		errorBody: 'The Help Centre did not answer. Try again in a moment.',
		emptyTitle: 'This guide has no steps yet',
		emptyBody: 'An editor has published the outline but not the steps. Search for a related guide meanwhile.',
		openFullGuide: 'Open full guide',
		roleNote: 'Only some roles can do this step',
	},

	callouts: {
		prerequisite: 'Prerequisite',
		billed: 'Billed',
		irreversible: 'Irreversible',
		tip: 'Tip',
	},

	search: {
		title: 'Search guides',
		results: (n: number, q: string) => (n === 1 ? `1 guide matches “${q}”` : `${n} guides match “${q}”`),
		typeMore: 'Type at least two characters to search.',
		noResultsTitle: (q: string) => `No guide matches “${q}”`,
		noResultsBody: 'Try fewer or different words — the screen’s own button labels usually work best.',
		closestModule: (m: string) => `Looking for ${m}?`,
		openModule: (m: string) => `Go to ${m}`,
		browseAll: 'Browse all guides',
		errorTitle: 'Search isn’t answering',
		errorBody: 'Try again in a moment, or browse the guides by topic.',
		prev: 'Previous',
		next: 'Next',
	},

	context: {
		panelTitle: 'Help for this page',
		aboutPage: 'About this page',
		guidesFor: (m: string) => `Guides for ${m}`,
		noGuidesTitle: 'No guides for this page yet',
		noGuidesBody: 'Search covers every guide you can open.',
		back: 'Back to guides',
		searchAll: 'Search all guides',
		openHelpCentre: 'Open the Help Centre',
		errorBody: 'Couldn’t load the guides for this page.',
		trigger: 'Help for this page',
	},

	palette: {
		trigger: 'Search guides',
		label: 'Search guides and pages',
		placeholder: 'Search guides or jump to a page…',
		guides: 'Guides',
		goTo: 'Go to',
		recent: 'Recent searches',
		clearRecent: 'Clear',
		searching: 'Searching guides…',
		noGuides: 'No guide matches yet — try the screen’s own words.',
		noPages: 'No page by that name.',
		hint: 'to select',
		hintNav: 'to move',
		hintClose: 'to close',
		guidesUnavailable: 'Guide search is unavailable — pages still work.',
	},

	nav: {
		help: 'Help & guides',
	},

	editor: {
		listTitle: 'Manage guides',
		listLede: 'Write, review and publish the guides in the Help Centre. Nothing publishes on save.',
		newGuide: 'New guide',
		sync: 'Sync modules from menu',
		syncDone: (r: { created: number; updated: number; unchanged: number; notInMenu: string[] }) =>
			[
				`Modules synced: ${r.created} new, ${r.updated} updated, ${r.unchanged} unchanged.`,
				r.notInMenu.length > 0
					? `${r.notInMenu.length} stored module${r.notInMenu.length === 1 ? ' is' : 's are'} no longer in the menu (${r.notInMenu.join(', ')}) — kept, not deleted.`
					: '',
			]
				.filter(Boolean)
				.join(' '),
		syncFailed: 'Couldn’t sync modules. Nothing was changed.',
		syncHint: 'Run after the sidebar changes, so each guide is hidden from exactly the people who cannot open its module.',
		filterModule: 'Module',
		filterState: 'State',
		allModules: 'All modules',
		allStates: 'All states',
		columns: { title: 'Guide', module: 'Module', state: 'State', updated: 'Updated' },
		hasDraft: 'Unpublished changes',
		listEmptyTitle: 'No guides yet',
		listEmptyBody: 'Sync the modules from the menu, then write the first guide.',
		listErrorTitle: 'Couldn’t load the guides',
		noAccessTitle: 'You can’t edit guides',
		noAccessBody: 'Editing needs the help_centre_manage permission. If it was granted recently, sign out and back in.',
		createTitle: 'New guide',
		slugHint: 'Lower-case words joined by hyphens. Appears in the guide’s link.',
		slugReserved: '“search” and “manage” are reserved — choose another slug.',
		slugInvalid: 'Use lower-case letters, numbers and hyphens only.',
		slugTaken: 'Another guide already uses this slug. Choose a different one.',
		unknownModule: 'That module has not been synced yet. Run “Sync modules from menu” first.',
		create: 'Create draft',
		cancel: 'Cancel',

		back: 'All guides',
		fields: {
			title: 'Title',
			titleHint: 'Name the goal, not the screen — “Approve agent applications in bulk”.',
			summary: 'Summary',
			module: 'Module',
			kind: 'Kind',
			estMinutes: 'Minutes to complete',
			prerequisites: 'Prerequisites',
			prerequisitesHint: 'One per line — what must be true before the first step.',
			callouts: 'Callouts',
			addCallout: 'Add callout',
			calloutType: 'Type',
			calloutText: 'Text',
			steps: 'Steps',
			addStep: 'Add step',
			stepTitle: 'Step title',
			stepBody: 'Instructions',
			stepExpect: 'You’ll see (expected result)',
			stepRoleNote: 'Role note (optional)',
			stepRoleNoteHint: 'e.g. “Super Admin only” — for a step not every reader can perform.',
			stepAnchor: 'UI anchor (optional)',
			stepAnchorHint: 'The data-help-anchor the guided tour will point at.',
			screenshot: 'Screenshot',
			screenshotAlt: 'Screenshot description (alt text)',
			screenshotAltHint: 'Describe what the screenshot shows. Required when a screenshot is attached.',
			moveUp: (n: number) => `Move step ${n} up`,
			moveDown: (n: number) => `Move step ${n} down`,
			remove: (n: number) => `Remove step ${n}`,
			removeCallout: (n: number) => `Remove callout ${n}`,
			audience: 'Who can read it',
			audienceHint:
				'Everyone who can open the module sees the guide. Narrow it only when a step is impossible for other roles. Takes effect when published.',
			requiredRoles: 'Only these roles (none ticked = any role)',
			extraActions: 'Also require one of these permissions (optional)',
			extraActionsHint: 'Comma-separated action codes, e.g. election_participants_manage.',
			changeNote: 'Change note (optional)',
			changeNoteHint: 'Recorded in the version history — what changed and why.',
		},
		altRequired: (n: number) => `Step ${n} has a screenshot with no description. Add alt text before saving.`,
		titleRequired: 'Give the guide a title before saving.',
		stepsRequired: 'Add at least one step before saving.',
		stepIncomplete: (n: number) => `Step ${n} needs a title and instructions.`,
		minutesRequired: 'Set the minutes to complete (1–600) before saving.',
		draftIncomplete: 'Add at least one step and the minutes to complete before publishing.',
		notPublished: 'This guide is not published, so there is nothing to unpublish. Reload to see its state.',
		nothingToPublish: 'Nothing to publish — the live guide already matches this content.',
		save: 'Save draft',
		saving: 'Saving…',
		saved: 'Draft saved',
		publish: 'Publish',
		publishing: 'Publishing…',
		published: 'Guide published',
		publishConfirm: 'Publish this guide? Everyone whose role can open its module will see it straight away.',
		publishDirtyConfirm:
			'Your unsaved changes will be saved first, then the guide is published for everyone whose role can open its module.',
		unpublish: 'Unpublish',
		unpublishConfirm: 'Unpublish this guide? Readers will no longer find it. The draft and history are kept.',
		unpublished: 'Guide unpublished',
		conflictTitle: 'Someone else saved this guide',
		conflictBody:
			'Another editor saved changes after you opened it. Reload to see their version — your unsaved edits here will be lost, so copy anything you need first.',
		reload: 'Reload guide',
		saveFailed: 'Couldn’t save the draft. Your edits are still here — try again.',
		publishFailed: 'Couldn’t publish. The draft is saved; try publishing again.',
		dirty: 'Unsaved changes',
		fixHighlighted: (n: number) =>
			n === 1
				? 'One field needs fixing before this can be saved — it is highlighted.'
				: `${n} fields need fixing before this can be saved — they are highlighted.`,
		lineError: (n: number, msg: string) => `Line ${n}: ${msg}`,
		tooLarge: 'This guide is over the 200 KB limit. Shorten the steps or split it into two guides.',
		leaveTitle: 'Unsaved changes',
		leaveConfirm: 'You have unsaved changes to this guide. Leave without saving?',
		leaveDiscard: 'Discard changes',
		edit: 'Edit',
		preview: 'Preview',
		previewNote: 'This is exactly how readers will see the guide.',
		history: 'Version history',
		restore: 'Restore',
		restoreConfirm:
			'Restore this version as the new draft? The current draft is replaced; nothing is published until you publish.',
		restoreDirtyConfirm:
			'Restore this version as the new draft? Your UNSAVED edits here are discarded and the current draft is replaced; nothing is published until you publish.',
		restoreLabel: (n: number) => `Restore version ${n}`,
		restored: 'Version restored as the draft',
		historyEmpty: 'No earlier versions.',
		previewAs: 'Preview as role',
		previewAsHint: 'See which modules and guides a role would find. Uses the server’s own filter.',
		previewRun: 'Show what they see',
		previewVisible: 'This guide is visible to them',
		previewHidden: 'This guide is hidden from them',
		previewNone: 'They would see no guides at all.',
		previewUnknown: (names: string[]) => `No role is called ${names.map((n) => `“${n}”`).join(', ')} — ignored.`,
		loadErrorTitle: 'Couldn’t load this guide',
		notFoundTitle: 'This guide doesn’t exist',
	},
};

export const HC_KIND_OPTIONS = [
	{ value: 'Guide', label: 'Guide (task, step by step)' },
	{ value: 'Concept', label: 'Concept (glossary entry)' },
];

export const HC_ROLE_OPTIONS = ['Super Admin', 'Administrator', 'Party Admin', 'Candidate', 'Result Analyst', 'Observer Admin'];
